import { BranchResponse } from '@/app/models/branch';
import { Project } from '@/app/models/project';
import { useBranchsAllQuery } from '@/app/services/branchApi';
import { usePeriodFileByPeriodQuery } from '@/app/services/periodFileApi';
import { useSearchProjectsQuery } from '@/app/services/projectApi';
import { useExportProviderMutation, useListProviderSearchTermQuery } from '@/app/services/providerApi';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import LateralDrawer from '@/components/common/LateralDrawer';
import TextButton from '@/components/common/TextButton';
import ProcessingTable from '@/components/Providers/ProcessingTable';
import NewEditValueProvider from '@/components/resourceDonors/providerEditScreen/NewEditValueProvider';
import { useBranch } from '@/context/BranchsContext';
import { usePeriod } from '@/context/PeriodContext';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Menu,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

function Providers() {
  const theme = useTheme();

  const { selectedPeriod } = usePeriod();
  const [drawerStatus, setDrawerStatus] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { t } = useTranslation('provider');
  const [originProject, setOriginProject] = useState<Project | null>(null);
  const [originProjectSearch, setOriginProjectSearch] = useState('');
  const { data: originProjects, isFetching: isLoadingOrigin } = useSearchProjectsQuery(
    {
      searchTerm: originProjectSearch,
      bringClosed: false,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    { skip: originProjectSearch.length < 1 },
  );

  const { selectedBranch, setSelectedBranch } = useBranch();

  const { data: files } = usePeriodFileByPeriodQuery(selectedPeriod?.id as number, {
    skip: !selectedPeriod,
  });
  const [exportFiles] = useExportProviderMutation();

  const handleExportMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    if (selectedPeriod) {
      exportFiles(selectedPeriod.date);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const { data: branchs } = useBranchsAllQuery();
  useEffect(() => {
    if (selectedBranch && branchs?.content && !branchs.content.find((b) => b.code === selectedBranch)) {
      setSelectedBranch('');
    }
  }, [branchs, selectedBranch, setSelectedBranch]);

  const { data, refetch } = useListProviderSearchTermQuery(
    {
      date: selectedPeriod?.date as string,
      searchTerm: originProject?.code || '',
      branch: selectedBranch || '',
    },
    {
      skip: !selectedPeriod,
    },
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        mr: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            minWidth: theme.spacing(31),
          }}
        >
          <Typography component="p" variant="fieldHeader">
            {t('common:project')}
          </Typography>
          <GenericAutoComplete
            value={originProject}
            onChange={(value) => {
              setOriginProject(value);
            }}
            equalityTest={(option, value) => option?.code === value?.code}
            getOptionLabel={(option) =>
              `${option?.code} - ${option?.name}${option?.branchName ? ` (${option?.branchName})` : ''}`
            }
            onSearch={(value) => {
              setOriginProjectSearch(value);
            }}
            options={originProjectSearch !== '' && originProjects?.content ? originProjects?.content : []}
            placeholder={t('common:enter.project.name.or.code') as string}
            loading={isLoadingOrigin}
            sx={{ width: theme.spacing(50) }}
          />
        </Box>

        <Box sx={{ minWidth: theme.spacing(31) }}>
          <Typography component="p" variant="fieldHeader">
            {t('common:branch')}
          </Typography>

          <FormControl sx={{ width: theme.spacing(30) }}>
            <Select
              displayEmpty
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              sx={{
                height: 32,
                fontSize: 14,
              }}
            >
              <MenuItem value="" sx={{ color: '#9e9e9e' }}>
                {t('common:select.branch')}
              </MenuItem>
              {branchs?.content.map((item: BranchResponse) => (
                <MenuItem key={item.code} value={item.code} sx={{ fontSize: 13 }}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box sx={{ ml: 'auto' }}>
        <TextButton text={`${t('export')}`} onClick={(e: React.MouseEvent<HTMLElement>) => handleExportMenuOpen(e)} />
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {!!files &&
            [...files.files]
              .sort((a, b) => a.fileType.localeCompare(b.fileType))
              .filter((file) => file.fileType === 'PROVIDER_CONFIG')
              .map((file) => (
                <MenuItem key={file.filePath} onClick={() => {}} href={file.filePath}>
                  <Button variant="text" sx={{ p: 0, color: 'primary' }} href={file.filePath}>
                    <DownloadIcon
                      fontSize="small"
                      sx={{
                        mr: 0.75,
                        color: theme.palette.text.primary,
                      }}
                    />
                    <Typography
                      sx={{
                        textTransform: 'none',
                        color: theme.palette.text.primary,
                      }}
                    >
                      {t(file.fileType)}
                    </Typography>
                  </Button>
                </MenuItem>
              ))}
          {!files && <CircularProgress sx={{ color: theme.palette.text.primary, mx: 4 }} />}
        </Menu>

        <TextButton
          sx={{ ml: 'auto' }}
          text={`+ ${t('add.value')}`}
          onClick={() => {
            setDrawerStatus(true);
          }}
        />
      </Box>

      <LateralDrawer open={drawerStatus} setOpen={setDrawerStatus} position="right" size="bigger">
        <NewEditValueProvider
          periodRequest={selectedPeriod?.date || ''}
          setDrawerOpen={setDrawerStatus}
          refetch={refetch}
        />
      </LateralDrawer>

      <ProcessingTable rows={data || []} />
    </Box>
  );
}

export default Providers;
